/**
 * data/repos/settingsRepo — repositório da área de configurações do usuário (Req 19).
 *
 * Expõe:
 *  - getProfile: deriva o perfil de /v1/auth/me (reutiliza o shape de sessionRepo,
 *    sem chamada duplicada — ambos lêem o mesmo endpoint). Em dev/preview segue o
 *    mesmo mecanismo de flip do sessionRepo.
 *
 * requestPasswordReset NÃO mora aqui: é um fluxo anônimo, anterior ao login, do
 * domínio de auth/sessão — não de configurações de um usuário já logado. Foi
 * movido para data/repos/authRepo.ts; ambos os chamadores (SettingsPage e
 * AzimAuthAdapter) importam de lá.
 *
 * Sobre 2FA: NÃO existe endpoint de 2FA no authentication service. A UI exibe
 * a seção como informativa/desabilitada. Não há chamada de rede para 2FA.
 *
 * Sobre "trocar senha logado": NÃO existe. O fluxo é redefinição por e-mail
 * (POST /v1/auth/password-reset). O SecurityPanel foi ajustado para refletir isso.
 */

import { apiClient } from "../../services/ApiClient.js";
import { resolveSource } from "../source.js";
import type { SessionContext } from "./sessionRepo.js";

// ---------------------------------------------------------------------------
// Tipos públicos do módulo de configurações
// ---------------------------------------------------------------------------

/**
 * Perfil exibido na página de configurações. Derivado de MeResponse;
 * campos sem fonte no backend ficam ausentes (undefined).
 */
export interface UserProfile {
  readonly userId: string;
  readonly email: string;
  readonly tenantId: string;
  readonly roles: readonly string[];
  /** Nome de exibição do usuário, vindo de /v1/auth/me (display_name).
   *  Quando ausente/vazio, fallback para a parte local do email. Sempre tem valor. */
  readonly displayName: string;
  /** Memberships do usuário (BU → papel). */
  readonly memberships: readonly { businessUnitId: string; role: string }[];
}

// ---------------------------------------------------------------------------
// DTO interno do /v1/auth/me (espelha sessionRepo — sem re-exportar o privado)
// ---------------------------------------------------------------------------

interface MeResponseDto {
  user_id: string;
  email: string;
  tenant_id: string;
  roles?: string[];
  memberships?: Array<{ bu_id: string; role: string }>;
  /** Nome de exibição retornado pelo /v1/auth/me (backend). Pode estar ausente/vazio. */
  display_name?: string;
}

// ---------------------------------------------------------------------------
// Funções públicas
// ---------------------------------------------------------------------------

/**
 * Carrega o perfil do usuário autenticado.
 *
 * Real → GET /v1/auth/me (área `session`).
 * Mock → projeta o usuário corrente de data/mock.ts.
 *
 * Reutiliza a mesma resolução de fonte do sessionRepo para não duplicar a
 * decisão mock↔api. Campos ausentes no DTO são normalizados como listas vazias.
 */
export async function getProfile(): Promise<UserProfile> {
  const source = await resolveSource("session");

  if (source.kind === "api") {
    const dto = await apiClient.get<MeResponseDto>("/auth/me");

    // Mapeia display_name com fallback resiliente: se vazio/nulo/ausente,
    // usa a parte local do email (antes do @).
    const displayName: string =
      dto.display_name?.trim() || dto.email.split("@")[0] || dto.email;

    return {
      userId: dto.user_id,
      email: dto.email,
      tenantId: dto.tenant_id,
      displayName,
      roles: dto.roles ?? [],
      memberships: (dto.memberships ?? []).map((m) => ({
        businessUnitId: m.bu_id,
        role: m.role,
      })),
    };
  }

  // Caminho mock: deriva de mock.currentUser (área session)
  if (!source.mock) throw new Error(`[repo] mock não disponível para área: ${source.area}`);
  const mock = source.mock;
  const ctx: SessionContext = {
    userId: mock.currentUser.id,
    email: mock.currentUser.email,
    tenantId: mock.currentTenantId,
    roles: ["tenant_admin"],
    memberships: [{ businessUnitId: mock.currentBUId, role: "tenant_admin" }],
  };

  // Fallback para displayName: display_name real, ou local-part do email, ou email completo
  const displayName: string =
    (mock.currentUser.displayName?.trim() || "") ||
    mock.currentUser.email.split("@")[0] ||
    mock.currentUser.email;

  return {
    userId: ctx.userId,
    email: ctx.email,
    tenantId: ctx.tenantId,
    displayName,
    roles: ctx.roles,
    memberships: ctx.memberships.map((m) => ({
      businessUnitId: m.businessUnitId,
      role: m.role,
    })),
  };
}
